<template>
  <b-modal
    v-model="showLocal"
    @hidden="close"
    dialog-class="home-section-editor"
    size="lg"
    centered
    no-close-on-backdrop
    hide-footer
    :title="modalTitle"
  >
    <form class="custom-form" @submit.prevent="save" novalidate>
      <!-- Comunes: título y subtítulo (el separador no tiene texto) -->
      <div class="row" v-if="form.type !== types.SEPARATOR.key">
        <div class="col-md-6">
          <div class="form-group">
            <label for="section-title">Título</label>
            <FormInput id="section-title" type="text" maxlength="200" placeholder="Opcional"
              v-model="form.title" />
          </div>
        </div>
        <div class="col-md-6" v-if="form.type !== types.FREE_TEXT.key">
          <div class="form-group">
            <label for="section-subtitle">Subtítulo</label>
            <FormInput id="section-subtitle" type="text" maxlength="500" placeholder="Opcional"
              v-model="form.subtitle" />
          </div>
        </div>
      </div>

      <!-- Modo y columnas: no aplican a texto libre, barra de cupón ni separador -->
      <div class="row" v-if="hasLayoutOptions">
        <div class="col-md-6">
          <div class="form-group">
            <label>Modo</label>
            <div class="d-flex">
              <b-form-radio v-for="mode in displayModes" :key="mode.key" v-model="form.displayMode"
                :value="mode.key" class="mr-4">
                {{ mode.label }}
              </b-form-radio>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label for="section-columns">Columnas</label>
            <FormSelect id="section-columns" v-model="form.columns" :items="columnOptions"
              defaultOption="Elegir columnas" />
          </div>
        </div>
      </div>
      <!-- BANNERS: se eligen los creados en Mantenimiento > Banners -->
      <section v-if="form.type === types.BANNERS.key">
        <div class="form-group">
          <label>Banners *</label>
          <p class="section-hint mb-2">
            Los banners se crean y editan en Mantenimiento &gt; Banners. Aquí solo se eligen; si
            luego se suspende uno, deja de mostrarse en la portada.
          </p>

          <FormInput type="text" placeholder="Filtrar por título" v-model="optionFilter" />

          <ul class="list-unstyled search-results mt-2" v-if="filteredBanners.length">
            <li v-for="banner in filteredBanners" :key="banner.id"
              class="d-flex justify-content-between align-items-center py-1">
              <span class="d-flex align-items-center">
                <img :src="bannerImageUrl(banner)" class="banner-thumb mr-2" alt=""
                  v-if="bannerImageUrl(banner)" />
                {{ bannerLabel(banner) }}
              </span>
              <button type="button" class="button button-light py-0" :disabled="isBannerSelected(banner.id)"
                @click="addBanner(banner)">
                {{ isBannerSelected(banner.id) ? "Agregado" : "Agregar" }}
              </button>
            </li>
          </ul>
          <p class="section-hint mt-2 mb-0" v-else-if="optionFilter">
            Ningún banner activo coincide con el filtro.
          </p>
          <p class="section-hint mt-2 mb-0" v-else>
            No hay banners activos. Créelos en Mantenimiento &gt; Banners.
          </p>

          <!-- Arrastrar para ordenar -->
          <draggable v-model="selectedBanners" class="mt-3" handle=".drag-handle" v-if="selectedBanners.length">
            <div v-for="banner in selectedBanners" :key="banner.id"
              class="selected-banner d-flex align-items-center">
              <i class="ri-draggable drag-handle mr-2"></i>
              <img :src="banner.imageUrl" class="banner-thumb mr-2" alt="" v-if="banner.imageUrl" />
              <span class="flex-1">{{ banner.name }}</span>
              <button type="button" class="border-0 bg-transparent text-danger p-0"
                @click="removeBanner(banner.id)">
                <i class="ri-close-circle-line"></i>
              </button>
            </div>
          </draggable>
        </div>
      </section>

      <!-- PRODUCTS: fuente, cantidad y selección -->
      <section v-if="form.type === types.PRODUCTS.key">
        <div class="form-group">
          <label>Fuente</label>
          <div class="d-flex flex-wrap">
            <b-form-radio v-for="source in productSources" :key="source.key" v-model="form.source"
              :value="source.key" class="mr-4 mb-1">
              {{ source.label }}
            </b-form-radio>
          </div>
        </div>

        <div class="form-group" v-if="form.source === sources.CATEGORY.key">
          <label for="section-category">Categoría *</label>
          <FormSelect id="section-category" v-model="form.sourceCategoryId" :items="categoryOptions"
            defaultOption="Elegir categoría" />
        </div>

        <div class="form-group">
          <label for="section-quantity">Cantidad de productos</label>
          <FormInput id="section-quantity" type="number" min="1" max="20" step="1" placeholder="8"
            v-model="form.quantity" />
        </div>

        <div class="form-group" v-if="form.source === sources.MANUAL.key">
          <label>Productos *</label>
          <FormInput type="text" placeholder="Buscar por nombre o código" v-model="productSearch" />
          <ul class="list-unstyled search-results mt-2" v-if="productResults.length">
            <li v-for="product in productResults" :key="product.id"
              class="d-flex justify-content-between align-items-center py-1">
              <span>{{ product.name }} <small class="text-muted">({{ product.code }})</small></span>
              <button type="button" class="button button-light py-0" :disabled="isProductSelected(product.id)"
                @click="addProduct(product)">
                {{ isProductSelected(product.id) ? "Agregado" : "Agregar" }}
              </button>
            </li>
          </ul>
          <!-- Arrastrar para ordenar -->
          <draggable v-model="selectedProducts" class="mt-2 d-flex flex-wrap" handle=".drag-handle">
            <span v-for="product in selectedProducts" :key="product.id" class="filter-item">
              <i class="ri-draggable drag-handle mr-1"></i>
              {{ product.name }}
              <button type="button" class="text-white p-0 bg-transparent border-0 ml-2"
                @click="removeProduct(product.id)">
                <i class="ri-close-circle-line"></i>
              </button>
            </span>
          </draggable>
        </div>
      </section>

      <!-- CATEGORIES y BRANDS: selección múltiple con filtro -->
      <section v-if="isReferenceList">
        <div class="form-group">
          <label>{{ form.type === types.CATEGORIES.key ? "Categorías" : "Marcas" }}</label>
          <FormInput type="text" placeholder="Filtrar por nombre" v-model="optionFilter" />
          <ul class="list-unstyled reference-options mt-2">
            <li v-for="option in filteredReferenceOptions" :key="option.id" :class="{ 'pl-4': option.child }">
              <b-form-checkbox v-model="selectedIds" :value="option.id">
                <span class="custom-checkbox-label">{{ option.name }}</span>
              </b-form-checkbox>
            </li>
          </ul>
          <small class="text-muted">{{ selectedIds.length }} seleccionadas</small>
        </div>
      </section>

      <!-- FREE_TEXT: contenido HTML -->
      <section v-if="form.type === types.FREE_TEXT.key">
        <div class="form-group">
          <label>Contenido</label>
          <global-trumbowyg v-model="form.subtitle" />
        </div>
      </section>

      <b-form-checkbox v-model="form.isVisible" switch class="mb-3">
        <span class="custom-checkbox-label">Visible en la tienda</span>
      </b-form-checkbox>

      <div class="form-group-buttons text-right mb-0">
        <button type="button" class="button button-light" @click="close()">Cancelar</button>
        <button type="submit" class="button button-primary" :disabled="saving">
          {{ saving ? "Guardando..." : "Guardar" }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { Alert, FormInput, FormSelect } from "@wizepe/wize-admin";
import draggable from "vuedraggable";
import { mapGetters } from "vuex";

import GlobalTrumbowyg from "@/core/components/common/GlobalTrumbowyg";
import { BannerService, BrandService, HomeSectionService, ProductService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

const DEFAULT_QUANTITY = 8;
// Tope del listado de banners disponibles; el endpoint pagina
const BANNERS_PAGE_SIZE = 100;

const emptyForm = (type) => ({
  type,
  title: null,
  subtitle: null,
  displayMode: LocalConstants.HomeDisplayModes.STATIC.key,
  columns: 3,
  source: LocalConstants.HomeProductSources.MANUAL.key,
  sourceCategoryId: null,
  quantity: DEFAULT_QUANTITY,
  isVisible: true,
});

export default {
  name: "HomeSectionEditor",
  components: {
    FormInput,
    FormSelect,
    GlobalTrumbowyg,
    draggable,
  },
  props: {
    show: Boolean,
    // Sección a editar; null al crear
    sectionId: [Number, String],
    // Tipo elegido al crear
    initialType: String,
  },
  data() {
    return {
      showLocal: false,
      saving: false,
      types: LocalConstants.HomeSectionTypes,
      sources: LocalConstants.HomeProductSources,
      displayModes: Object.values(LocalConstants.HomeDisplayModes),
      productSources: Object.values(LocalConstants.HomeProductSources),
      form: emptyForm(null),
      // Banners activos de Mantenimiento y los elegidos para esta sección, en orden
      availableBanners: [],
      selectedBanners: [],
      selectedProducts: [],
      selectedIds: [],
      productSearch: "",
      productResults: [],
      productSearchTimer: null,
      optionFilter: "",
      brands: [],
    };
  },
  computed: {
    ...mapGetters({
      categories: "app/getCategories",
    }),
    modalTitle() {
      const type = this.types[this.form.type];
      const label = type ? type.label : "Sección";
      return this.sectionId ? `Editar sección: ${label}` : `Nueva sección: ${label}`;
    },
    // Productos: hasta 5 por fila (como la cuadrícula de categoría en la tienda); el resto, hasta 4
    columnOptions() {
      const values = this.form.type === this.types.PRODUCTS.key ? [2, 3, 4, 5] : [1, 2, 3, 4];
      return values.map((value) => ({ key: value, label: `${value}` }));
    },
    hasLayoutOptions() {
      return ![this.types.SEPARATOR.key, this.types.FREE_TEXT.key, this.types.COUPON_BAR.key]
        .includes(this.form.type);
    },
    isReferenceList() {
      return [this.types.CATEGORIES.key, this.types.BRANDS.key].includes(this.form.type);
    },
    filteredBanners() {
      const term = (this.optionFilter || "").toLowerCase().trim();
      if (!term) return this.availableBanners;
      return this.availableBanners.filter((banner) => this.bannerLabel(banner).toLowerCase().includes(term));
    },
    // Categorías padre y debajo sus hijas
    categoryList() {
      const parents = this.categories.filter((c) => !c.parent);
      return parents.reduce((acc, parent) => {
        acc.push({ id: parent.id, name: parent.name, child: false });
        this.categories
          .filter((c) => c.parent && c.parent.id === parent.id)
          .forEach((c) => acc.push({ id: c.id, name: c.name, child: true }));
        return acc;
      }, []);
    },
    categoryOptions() {
      return this.categoryList.map((c) => ({ key: c.id, label: c.child ? `— ${c.name}` : c.name }));
    },
    filteredReferenceOptions() {
      const options = this.form.type === this.types.CATEGORIES.key
        ? this.categoryList
        : this.brands.map((b) => ({ id: b.id, name: b.name, child: false }));
      const term = (this.optionFilter || "").toLowerCase().trim();
      return term ? options.filter((o) => o.name.toLowerCase().includes(term)) : options;
    },
  },
  watch: {
    show(value) {
      this.showLocal = value;
      if (value) this.open();
    },
    productSearch(value) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => this.searchProducts(value), 400);
    },
  },
  methods: {
    async open() {
      this.form = emptyForm(this.initialType);
      this.selectedBanners = [];
      this.selectedProducts = [];
      this.selectedIds = [];
      this.productSearch = "";
      this.productResults = [];
      this.optionFilter = "";

      await Promise.all([this.$store.dispatch("app/loadCategories"), this.loadBrands(), this.loadBanners()]);
      if (this.sectionId) await this.load();
    },
    async load() {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await HomeSectionService.get(this.sectionId);
        const section = response.payload;

        this.form = {
          type: section.type,
          title: section.title,
          subtitle: section.subtitle,
          displayMode: section.displayMode || this.displayModes[0].key,
          columns: section.columns || 3,
          source: section.source || this.sources.MANUAL.key,
          sourceCategoryId: section.sourceCategoryId,
          quantity: section.quantity || DEFAULT_QUANTITY,
          isVisible: section.isVisible !== false,
        };

        const items = section.items || [];
        if (section.type === this.types.BANNERS.key) {
          // El nombre y la miniatura vienen resueltos: no dependen de la página del listado
          this.selectedBanners = items
            .filter((item) => item.referenceId)
            .map((item) => ({
              id: item.referenceId,
              name: item.referenceName || `Banner #${item.referenceId}`,
              imageUrl: item.referenceImageUrl,
            }));
        } else if (section.type === this.types.PRODUCTS.key) {
          this.selectedProducts = items
            .filter((item) => item.referenceId)
            .map((item) => ({ id: item.referenceId, name: item.referenceName || `Producto #${item.referenceId}` }));
        } else if (this.isReferenceList) {
          this.selectedIds = items.filter((item) => item.referenceId).map((item) => item.referenceId);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async loadBrands() {
      if (this.brands.length) return;
      try {
        const response = await BrandService.listActive();
        this.brands = response.payload || [];
      } catch (error) {
        console.error(error);
      }
    },
    // Solo los activos: un banner suspendido no se muestra en la tienda
    async loadBanners() {
      if (this.availableBanners.length) return;
      try {
        const response = await BannerService.list({ isActive: true, page: 0, size: BANNERS_PAGE_SIZE });
        this.availableBanners = response.payload.content || [];
      } catch (error) {
        console.error(error);
      }
    },
    // --- Productos elegidos a mano ---
    async searchProducts(term) {
      this.productResults = await this.findProducts(term);
    },
    async findProducts(term) {
      if (!term || term.trim().length < 2) return [];
      try {
        const response = await ProductService.list({ search: term.trim(), page: 0, size: 10 });
        return response.payload.content || [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    isProductSelected(id) {
      return this.selectedProducts.some((product) => product.id === id);
    },
    addProduct(product) {
      if (this.isProductSelected(product.id)) return;
      this.selectedProducts.push({ id: product.id, name: product.name });
    },
    removeProduct(id) {
      this.selectedProducts = this.selectedProducts.filter((product) => product.id !== id);
    },
    // --- Banners elegidos de Mantenimiento ---
    bannerLabel(banner) {
      return banner.title || `Banner #${banner.id}`;
    },
    bannerImageUrl(banner) {
      if (banner.desktopImage && banner.desktopImage.url) return banner.desktopImage.url;
      return banner.mobileImage && banner.mobileImage.url ? banner.mobileImage.url : null;
    },
    isBannerSelected(id) {
      return this.selectedBanners.some((banner) => banner.id === id);
    },
    addBanner(banner) {
      if (this.isBannerSelected(banner.id)) return;
      this.selectedBanners.push({
        id: banner.id,
        name: this.bannerLabel(banner),
        imageUrl: this.bannerImageUrl(banner),
      });
    },
    removeBanner(id) {
      this.selectedBanners = this.selectedBanners.filter((banner) => banner.id !== id);
    },
    // --- Guardar ---
    buildItems() {
      const type = this.form.type;
      if (type === this.types.BANNERS.key) {
        return this.selectedBanners.map((banner, index) => ({ referenceId: banner.id, position: index + 1 }));
      }
      if (type === this.types.PRODUCTS.key && this.form.source === this.sources.MANUAL.key) {
        return this.selectedProducts.map((product, index) => ({ referenceId: product.id, position: index + 1 }));
      }
      if (this.isReferenceList) {
        return this.selectedIds.map((id, index) => ({ referenceId: id, position: index + 1 }));
      }
      return [];
    },
    validate() {
      if (this.form.type === this.types.BANNERS.key && !this.selectedBanners.length) {
        return "Elija al menos un banner";
      }
      if (this.form.type === this.types.PRODUCTS.key) {
        if (this.form.source === this.sources.MANUAL.key && !this.selectedProducts.length) {
          return "Seleccione al menos un producto";
        }
        if (this.form.source === this.sources.CATEGORY.key && !this.form.sourceCategoryId) {
          return "Seleccione la categoría";
        }
      }
      if (this.isReferenceList && !this.selectedIds.length) {
        return "Seleccione al menos un elemento";
      }
      return null;
    },
    async save() {
      const error = this.validate();
      if (error) {
        Alert.error(error);
        return;
      }

      const isProducts = this.form.type === this.types.PRODUCTS.key;
      const quantity = parseInt(this.form.quantity, 10);
      const payload = {
        ...this.form,
        columns: this.form.columns ? Number(this.form.columns) : null,
        displayMode: this.hasLayoutOptions ? this.form.displayMode : null,
        source: isProducts ? this.form.source : null,
        sourceCategoryId: isProducts && this.form.source === this.sources.CATEGORY.key
          ? this.form.sourceCategoryId : null,
        quantity: isProducts && !Number.isNaN(quantity) ? Math.min(Math.max(quantity, 1), 20) : null,
        items: this.buildItems(),
      };

      try {
        this.saving = true;
        const response = this.sectionId
          ? await HomeSectionService.update(this.sectionId, payload)
          : await HomeSectionService.create(payload);

        // Los errores de validación del backend llegan con HTTP 200 y status BAD_REQUEST
        if (response.status !== "OK") {
          Alert.error("Revise los datos de la sección");
          return;
        }
        Alert.success("Sección guardada");
        this.$emit("saved", response.payload);
        this.close();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.showLocal = false;
      this.$emit("hide");
    },
  },
};
</script>

<style lang="stylus" scoped>
@import "~@wizepe/wize-admin/dist/styles/variables"

.section-hint
  font-size: 12px
  color: color-light-grey

.banner-thumb
  width: 56px
  height: 32px
  object-fit: cover
  border-radius: 4px
  border: 1px solid #e8eaed

.selected-banner
  border: 1px solid #e3e5ea
  border-radius: 8px
  padding: 8px 12px
  margin-bottom: 8px
  font-size: 13px

  .flex-1
    flex: 1

.search-results,
.reference-options
  max-height: 220px
  overflow-y: auto
  border: 1px solid #e8eaed
  border-radius: 6px
  padding: 4px 10px

.drag-handle
  cursor: grab

.add-link
  color: color-light-grey

.custom-checkbox-label
  font-size: 13px !important
</style>
